/**
 * Gestion des accès menu / routes pour le frontend SGR (Volt).
 * Codes alignés sur la Liste Accès superviseur.
 */

export function getStoredUser() {
  try {
    const raw = localStorage.getItem("user_profile");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isAdmin(user = getStoredUser()) {
  if (!user) return false;
  return user.role === "COMPANY_ADMIN" || user.role === "SUPERADMIN";
}

export function isSupervisor(user = getStoredUser()) {
  return user?.role === "SUPERVISOR";
}

/**
 * true si l'utilisateur a le code exact, un ancêtre (ex: "1" couvre "1.2"),
 * ou le joker admin "*".
 */
export function hasAccess(code, user = getStoredUser()) {
  if (!user) return false;
  const codes = user.access_codes || [];
  if (codes.includes("*") || isAdmin(user)) return true;
  if (user.role !== "SUPERVISOR") return false;
  if (!code) return false;
  if (codes.includes(code)) return true;

  // Ancêtre : "1" autorise "1.2" / "1.2.3"
  const parts = String(code).split(".");
  for (let i = parts.length - 1; i >= 1; i -= 1) {
    const parent = parts.slice(0, i).join(".");
    if (codes.includes(parent)) return true;
  }
  return false;
}

export function hasAnyAccess(codeList, user = getStoredUser()) {
  return (codeList || []).some((c) => hasAccess(c, user));
}

/** Affiche une section menu si le code section OU un enfant est autorisé */
export function canSeeSection(sectionCode, childCodes = [], user = getStoredUser()) {
  if (isAdmin(user)) return true;
  if (hasAccess(sectionCode, user)) return true;
  return hasAnyAccess(childCodes, user);
}

/** Mapping path → codes d'accès requis (au moins un) */
export const ROUTE_ACCESS = {
  "/dashboard/overview": ["0", "0.1", "0.2", "0.3", "0.4", "0.5", "0.6"],
  "/settings": ["0", "0.1"], // mon compte toujours utile ; admin bypass
  "/branches": ["0.6", "3.4"],
  "/agents": ["1", "1.1", "1.2", "1.3", "1.4", "2", "2.1", "2.2", "2.3", "2.4", "2.5", "2.6", "2.7", "2.8", "2.9"],
  "/configurations/mariage-gratuit": ["3.8"],
  "/configurations/tirages": ["3.7"],
  "/configurations/primes": ["3.5"],
  "/surveillance/statistiques": ["4", "4.1", "4.2"],
  "/surveillance/blocage-boule": ["4", "4.6", "4.6.1", "4.6.2"],
  "/surveillance/limite-boule": ["4", "4.6", "4.6.1", "4.6.2"],
  "/surveillance/limites": ["4", "4.7", "4.7.1", "4.7.2", "4.7.3"],
  "/surveillance/controle": ["4", "4.8", "4.8.1", "4.8.2", "4.8.3", "4.8.4"],
  "/surveillance/lots-gagnant": ["4", "4.4", "4.4.1"],
  "/rapport/journalier": ["5", "5.1"],
  "/rapport/general": ["5", "5.1"],
  "/rapport/fiches-vendues": ["5", "5.2"],
  "/rapport/fiches-gagnantes": ["5", "5.3"],
  "/rapport/fiches-eliminees": ["5", "5.4"],
};

export function canAccessPath(pathname, user = getStoredUser()) {
  if (isAdmin(user)) return true;
  if (!user) return false;
  if (user.role === "AGENT") return false;

  // Mon compte : accessible à tout superviseur connecté
  if (pathname === "/settings" || pathname === "/settings/") return true;

  const required = ROUTE_ACCESS[pathname];
  if (!required) {
    // Routes non listées : admin only
    return false;
  }
  return hasAnyAccess(required, user);
}

export function getDefaultLandingPath(user = getStoredUser()) {
  if (!user) return "/examples/sign-in";
  if (isAdmin(user)) return "/dashboard/overview";
  if (user.role === "SUPERVISOR") {
    const candidates = [
      "/dashboard/overview",
      "/agents",
      "/rapport/journalier",
      "/surveillance/statistiques",
      "/branches",
      "/settings",
    ];
    for (const p of candidates) {
      if (canAccessPath(p, user)) return p;
    }
    return "/settings";
  }
  return "/examples/sign-in";
}

export function updateFaviconAndTitle(user = getStoredUser()) {
  try {
    if (!user || !user.company) return;
    const { name, logo } = user.company;

    if (name) {
      document.title = `${name} - Administration`;
    }

    if (logo) {
      let fullUrl = logo;
      if (!fullUrl.startsWith("http") && !fullUrl.startsWith("data:")) {
        const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? window.location.origin
          : 'https://sgrloto.com';
        fullUrl = `${API_BASE}${fullUrl.startsWith('/') ? '' : '/'}${fullUrl}`;
      }

      // Update or create favicon link tag
      let links = document.querySelectorAll("link[rel*='icon']");
      if (links.length === 0) {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        document.head.appendChild(newLink);
        links = [newLink];
      }
      links.forEach((link) => {
        link.href = fullUrl;
      });
    }
  } catch (e) {
    console.error("Error updating dynamic favicon:", e);
  }
}
